import { cn } from "@/components/podkit/lib/cn";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { WarmPoolStatusIcon } from "@/components/projects/WarmPoolStatusIcon";
import { Switch } from "@/components/Switch";
import { Input } from "@/components/podkit/forms/Input";
import { Text } from "@/components/podkit/typography/Text";
import {
    isWarmPoolDeleting,
    WARM_POOL_MIN_SIZE,
    WARM_POOL_MAX_SIZE,
    type PlainWarmPool,
} from "@/queries/warm-pool-queries";
import { useDebouncedCallback } from "use-debounce";
import { toast as showToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

export type WarmPoolExpandedRowProps = {
    envClassId: string;
    warmPoolEnabled: boolean;
    warmPoolSize: number;
    warmPool?: PlainWarmPool;
    onWarmPoolToggle: (enabled: boolean) => void;
    onWarmPoolSizeChange: (size: number) => Promise<void>;
    onLocalSizeChange: (size: number) => void;
    disabled?: boolean;
    isMutating?: boolean;
};

export const WarmPoolExpandedRow: FC<WarmPoolExpandedRowProps> = ({
    envClassId,
    warmPoolEnabled,
    warmPoolSize,
    warmPool,
    onWarmPoolToggle,
    onWarmPoolSizeChange,
    onLocalSizeChange,
    disabled,
    isMutating,
}) => {
    const poolShuttingDown = !!warmPool && isWarmPoolDeleting(warmPool);
    const isPending = isMutating || poolShuttingDown;

    // Inline "Saved" indicator that auto-hides
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
    const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const debouncedSave = useDebouncedCallback(
        async (size: number) => {
            setSaveStatus("saving");
            try {
                await onWarmPoolSizeChange(size);
                setSaveStatus("saved");
                if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
                hideTimerRef.current = setTimeout(() => setSaveStatus("idle"), 1500);
            } catch (error) {
                setSaveStatus("idle");
                showToast({
                    title: "Failed to update pool size",
                    description: formatError(error),
                });
            }
        },
        500,
    );

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        };
    }, [debouncedSave]);

    const handleSizeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            if (raw === "") return;

            const val = parseInt(raw, 10);
            if (isNaN(val)) return;

            // Clamp to valid range instead of ignoring out-of-range values,
            // so the input stays in sync with state.
            const clamped = Math.max(WARM_POOL_MIN_SIZE, Math.min(WARM_POOL_MAX_SIZE, val));
            onLocalSizeChange(clamped);
            void debouncedSave(clamped);
        },
        [onLocalSizeChange, debouncedSave],
    );

    return (
        <div className="flex items-center gap-3 border-t border-border-light bg-surface-secondary px-4 py-2 pl-10">
            <Switch
                id={`warm-pool-toggle-${envClassId}`}
                state={isPending ? "pending" : warmPoolEnabled ? "checked" : "unchecked"}
                onToggle={onWarmPoolToggle}
                disabled={disabled || poolShuttingDown}
                aria-label="Enable warm pool"
                data-testid={`warm-pool-toggle-${envClassId}`}
            />
            <Text className="text-sm font-medium text-content-primary">Warm Pool</Text>
            {warmPoolEnabled && !poolShuttingDown && (
                <>
                    <div className="flex items-center gap-1.5">
                        <Text className="text-sm text-content-secondary">Size:</Text>
                        <Input
                            type="number"
                            min={WARM_POOL_MIN_SIZE}
                            max={WARM_POOL_MAX_SIZE}
                            value={warmPoolSize}
                            onChange={handleSizeChange}
                            disabled={disabled || isMutating}
                            className="h-7 w-16 text-sm"
                            data-testid="warm-pool-size-input"
                        />
                    </div>
                    <span
                        className={cn(
                            "text-sm transition-opacity duration-300",
                            saveStatus === "saving" && "text-content-secondary opacity-100",
                            saveStatus === "saved" && "text-content-green opacity-100",
                            saveStatus === "idle" && "opacity-0",
                        )}
                        aria-live="polite"
                    >
                        {saveStatus === "saving" ? "Saving…" : "Saved"}
                    </span>
                    {warmPool && <WarmPoolStatusIcon warmPool={warmPool} />}
                </>
            )}
            {poolShuttingDown && (
                <Text className="text-sm text-content-secondary">Shutting down…</Text>
            )}
        </div>
    );
};
